import cn from 'classnames';
import { useCallback, useState } from 'preact/hooks';
import Carousel from './parts/Carousel/Carousel.tsx';
import FullModalView from './parts/FullModalView/FullModalView.tsx';
import {
  mediaGalleryDefaultConfig as baseConfig,
  type MediaGalleryConfig
} from './MediaGallery.config.ts';
import styles from './MediaGallery.module.scss';

export type MediaGalleryProps = {
  class?: string;
  /**
   * Must supply at least 1 set of images.
   * Note: Videos do not require the hires image array.
   * It is possible to mix videos and images.
   * All lists are associative arrays and need to have the images in the right order.
   */
  media: {
    /**
     * List of main media items for the slider and fullscreen.
     */
    items: MediaGalleryItem[];
    /**
     * Support for low-res thumbs, which are different from the main images
     * If not provided, main images will be used as thumbnails
     */
    thumbnails?: MediaGalleryItem[];
    /**
     * Support for hi-res images, which are different from the main images
     * If not provided, main images will be used as zoom/magnified image
     */
    hires?: MediaGalleryItem[];
  };
  config?: MediaGalleryConfig;
  /** Triggered when user views an image in the carousel */
  onCarouselImageViewed?: (
    viewedIndex: number,
    totalNumberOfImages: number,
    eventSource: GalleryEventSourceType
  ) => void;
  /** Triggered when user views a large image in the gallery */
  onGalleryImageViewed?: (
    viewedIndex: number,
    totalNumberOfImages: number,
    eventSource: GalleryEventSourceType
  ) => void;
  /** Triggered when user views a magnified image in the gallery */
  onMagnifiedImageViewed?: (
    viewedIndex: number,
    totalNumberOfImages: number
  ) => void;
};
export default function MediaGallery({
  media,
  onCarouselImageViewed = () => {},
  onGalleryImageViewed = () => {},
  onMagnifiedImageViewed = () => {},
  config: customConfig = {
    translations: baseConfig.translations,
    hasReducedMotion: baseConfig.hasReducedMotion
  },
  class: className
}: MediaGalleryProps) {
  const { items, thumbnails = items } = media;
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  /** The current index of the carousel */
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  /** Close modal handler */
  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, [isFullscreen]);

  const config: MediaGalleryConfig = {
    ...baseConfig,
    ...customConfig
  };

  if (!items || items.length === 0) return null;

  return (
    <>
      <div class={cn(styles.container, className)}>
        <Carousel
          items={items}
          thumbnails={thumbnails}
          onClick={(newIndex) => {
            setCurrentIndex(newIndex);
            if (items[newIndex].type === 'IMAGE') {
              setIsFullscreen(true);
            }
          }}
          config={config}
          onImageViewed={(viewedIndex, eventSource) =>
            onCarouselImageViewed(viewedIndex, media.items.length, eventSource)
          }
        />
      </div>
      <FullModalView
        config={config}
        isOpen={isFullscreen}
        media={media}
        currentIndex={currentIndex}
        onClose={closeFullscreen}
        onNextPrev={(_e, clickedIndex) => setCurrentIndex(clickedIndex)}
        onThumbnailClick={(_e, clickedIndex) => setCurrentIndex(clickedIndex)}
        onGalleryImageViewed={(viewedIndex, eventSource) =>
          onGalleryImageViewed(viewedIndex, media.items.length, eventSource)
        }
        onMagnifiedImageViewed={(viewedIndex) =>
          onMagnifiedImageViewed(viewedIndex, media.items.length)
        }
      />
    </>
  );
}

/**
 * Describes the interactable item which can change the carousel slide or main image that is being shown. Used for tracking.
 *
 * `chevron` = user clicks on left or right arrow in carousel or fullscreen gallery
 * `thumbnail` = user clicks on thumbnail in carousel or fullscreen gallery
 * `default` = user triggers carousel or fullscreen gallery change with keyboard or swiping on carousel
 */
export type GalleryEventSourceType = 'default' | 'chevron' | 'thumbnail';
export type MediaGalleryItem = {
  type: MediaGalleryItemType;
  url: string;
  alt: string;
  /** Poster image for videos */
  poster?: string;
};
export type MediaGalleryItemType = 'IMAGE' | 'VIDEO';
